/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { getLanguageMatch, getUserLanguageCode } from 'js/lib/language';
import { languageTagToLocalName } from 'js/lib/languageLocalized';
import { useRetracked } from 'js/lib/retracked';

import { MenuList, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { Language } from 'bundles/composable-page-header/legacy/translation/useCourseLanguageQuery';
import { useSetUserLocaleMutation } from 'bundles/composable-page-header/legacy/translation/useSetUserLocaleMutation';

import _t from 'i18n!nls/page';

type Props = { language: Language; currentLanguage: string };

const styles = {
  label: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
};

export const TranslationDropdownMenuItem = ({ language, currentLanguage }: Props): React.ReactElement => {
  const retracked = useRetracked();
  const track = useTracker();

  const setUserLocale = useSetUserLocaleMutation();

  return (
    <MenuList.Item
      checked={!!getLanguageMatch(language.key, currentLanguage)}
      role="menuitemradio"
      onClick={async () => {
        retracked({
          trackingName: 'change_language',
          action: 'click',
          trackingData: {
            previousLanguage: getUserLanguageCode(),
            language: language.key,
          },
        });
        track('switch_language', {
          previousLanguage: currentLanguage,
          currentLanguage: language.key,
        });

        await setUserLocale(language.key);
        location.reload();
      }}
    >
      <div css={styles.label}>
        <Typography2 variant="bodyPrimary" component="div">
          {languageTagToLocalName(language.key)}
        </Typography2>
        {language.isOriginal && (
          <Typography2 variant="bodyPrimary" color="supportText" component="div">
            ({_t('original')})
          </Typography2>
        )}
      </div>
    </MenuList.Item>
  );
};

export default TranslationDropdownMenuItem;
